import { Message } from "@/components/CodeGen/Chat/Message";
import { useToast } from "@/hooks/use-toast";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useCurrentWebSocketError } from "@/lib/hooks/useCurrentWebSocketError";

import { getMcpServersQueryOptions } from "@/lib/hooks/useMcpServers";
import { getProjectQueryOptions } from "@/lib/hooks/useProjects";
import { getSecretsQueryOptions } from "@/lib/hooks/useSecrets";
import { useMessageStore } from "@/state/stores";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useShallow } from "zustand/shallow";

export const Route = createFileRoute("/(codegen)/codegen/$projectId")({
  component: ProjectPage,
  loader: async ({ context: { queryClient }, params: { projectId } }) => {
    // Prefetch project data, MCP servers, and secrets data in parallel
    await Promise.all([
      queryClient.ensureQueryData(getProjectQueryOptions(projectId)),
      queryClient.ensureQueryData(getMcpServersQueryOptions(projectId)),
      queryClient.ensureQueryData(getSecretsQueryOptions(projectId)),
    ]);
  },
});

function ProjectPage() {
  const projectId = useActiveProjectId();
  const getCurrentError = useCurrentWebSocketError();
  const { toast } = useToast();

  const messageIds = useMessageStore(
    useShallow(({ messages }) => messages.map(({ id }) => id)),
  );

  // This shouldn't happen, but is here to satisfy the type checker
  if (!projectId) {
    throw new Error("No project ID available");
  }

  // Show toast when error occurs
  useEffect(() => {
    const errorEvent = getCurrentError?.(); // Only calls if error exists
    if (errorEvent) {
      toast({
        variant: "destructive",
        title: "Chat Error",
        description: errorEvent.error,
        duration: 5000,
      });
    }
  }, [getCurrentError, toast]);

  return (
    <div className="flex flex-col gap-5">
      {messageIds.map((messageId) => (
        <Message
          key={messageId}
          messageId={messageId}
          projectId={projectId}
          isFinalMessage={messageId === messageIds[messageIds.length - 1]}
        />
      ))}
    </div>
  );
}

export default ProjectPage;
